<template>
  <modal-template ref="modal" :title="title" type-modal="export_file_modal">
    <div class="modal-content__main">
      <d-form>
        <d-form-row class="mb-4">
          <d-col cols="6">
            <d-checkbox
              class="custom-toggle-sm mb-2"
              v-for="(config, typeKey) in currentConfig.types"
              :key="typeKey"
              v-model="selectedTypes[typeKey]"
            >
              {{ config.label }}
            </d-checkbox>
          </d-col>

          <d-col cols="6">
            <label>Formato do arquivo:</label>
            <div class="ml-2">
              <d-radio
                v-model="formatMode"
                name="format-mode"
                value="svg"
                class="fs-6 text-muted"
              >
                SVG
              </d-radio>
              <d-radio
                v-model="formatMode"
                name="format-mode"
                value="eps"
                class="fs-6 text-muted"
              >
                EPS
              </d-radio>
            </div>
          </d-col>
        </d-form-row>

        <d-form-row>
          <d-col cols="6" class="mb-2" v-if="currentConfig.toggles.length">
            <label>Estilo:</label>
            <d-checkbox
              v-for="toggle in currentConfig.toggles"
              :key="toggle.key"
              toggle
              v-model="exportOptions[toggle.key]"
              class="custom-toggle-sm"
            >
              <span class="mr-1">{{ toggle.label }}</span>
            </d-checkbox>
          </d-col>

          <d-col cols="6" class="mb-3" v-if="currentConfig.radioGroups.length">
            <div v-for="group in currentConfig.radioGroups" :key="group.key">
              <label>{{ group.label }}</label>
              <div class="ml-2 mt-1">
                <d-radio
                  v-for="(choice, index) in group.choices"
                  :key="choice.value"
                  v-model="exportOptions[group.key]"
                  :name="group.key"
                  :value="choice.value"
                  :class="
                    index < group.choices.length - 1
                      ? 'mr-3 mb-2 fs-7'
                      : 'mb-0 fs-7'
                  "
                >
                  {{ choice.label }}
                </d-radio>
              </div>
            </div>
          </d-col>
        </d-form-row>
      </d-form>
    </div>
    <div slot="actions" class="modal-content__control">
      <d-button
        type="button"
        @click="onCancel"
        class="btn btn-sm btn-secondary mr-2"
      >
        Cancelar
      </d-button>
      <d-button type="button" @click="onConfirm" class="btn btn-sm btn-primary">
        Confirmar
      </d-button>
    </div>
  </modal-template>
</template>

<script>
import { mapActions } from 'vuex';
import config from '_config';
import ModalTemplate from '_shared/Modal.vue';
import PuzzleService from '_services/rest/modules/puzzle';
import { EXPORT_CONFIG, GAME_TYPES } from '_utils/svgExport/exportConfig';
import { ExportManager } from '_utils/svgExport/exportManager';

export default {
  name: 'export-file-modal',
  components: { ModalTemplate },
  props: {
    gameType: {
      type: String,
      required: true,
      validator: value => Object.values(GAME_TYPES).includes(value),
    },
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    loadCompleteItems: {
      type: Boolean,
      default: false,
    },
    fileName: { type: String, required: false, default: 'exportacao' },
  },
  data() {
    return {
      title: 'Exportar arquivos',
      formatMode: 'eps',
      selectedTypes: {},
      exportOptions: {
        transparentBackground: false,
        arrowHeadStyle: 'filled',
        arrowAlignment: 'center',
        borderRadius: 0,
        colored: false,
        exportPathWeight: 'normal',
        exportBank: true,
        batchSize: config.PUZZLES_BATCH_SIZE || 50,
      },
    };
  },
  computed: {
    currentConfig() {
      return EXPORT_CONFIG[this.gameType];
    },
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert', 'showLoader', 'hideLoader']),
    onCancel() {
      this.setToggle({ type: 'export_file_modal', bool: false });
    },
    async onConfirm() {
      const { batchSize } = this.exportOptions;

      try {
        const exportItems = await this.getExportItems();
        const totalItems = exportItems.length;
        const prefix = this.generatePrefix(totalItems);

        if (totalItems > batchSize) {
          await this.runBatchExport(exportItems, prefix, batchSize);
        } else {
          await this.runSingleExport(exportItems, prefix);
        }

        this.finalizeExport();
      } catch (error) {
        this.handleExportError(error);
      }
    },
    async getExportItems() {
      if (!this.loadCompleteItems) {
        return this.items;
      }

      return Promise.all(
        this.items.map(item => PuzzleService.getPuzzleById(item.id)),
      );
    },
    async runBatchExport(items, basePrefix, batchSize) {
      const totalBatches = Math.ceil(items.length / batchSize);

      for (let i = 0; i < totalBatches; i += 1) {
        const startIndex = i * batchSize;
        const currentBatch = items.slice(startIndex, startIndex + batchSize);

        // eslint-disable-next-line no-await-in-loop
        await this.executeExport({
          items: currentBatch,
          startIndex,
          prefix: `${basePrefix}_part_${i + 1}`,
          loaderLabel: `[Batch ${i + 1}/${totalBatches}]`,
        });

        if (i < totalBatches - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => setTimeout(resolve, 600));
        }
      }
    },
    async runSingleExport(items, prefix) {
      return this.executeExport({ items, prefix });
    },
    async executeExport({
      items, startIndex = 0, prefix, loaderLabel = '',
    }) {
      return ExportManager.processExport({
        items,
        startIndex,
        prefix,
        gameType: this.gameType,
        configMap: EXPORT_CONFIG,
        formatMode: this.formatMode,
        exportOptions: this.exportOptions,
        selectedTypes: this.selectedTypes,
        hooks: {
          showLoader: msg => this.showLoader(`${loaderLabel} ${msg}`.trim()),
          showError: err =>
            this.activeAlert({
              message: err,
              theme: 'danger',
            }),
        },
      });
    },
    generatePrefix(totalItems) {
      const exportId = `${Date.now()}${Math.floor(Math.random() * 100000)}`;
      return totalItems > 1
        ? `games_${exportId}`
        : `${this.fileName}-${exportId}`;
    },
    finalizeExport() {
      this.hideLoader();
      this.onCancel();
    },
    handleExportError(error) {
      this.hideLoader();
      console.error('Export error:', error);
      this.activeAlert({
        message: 'Ocorreu um erro inesperado na exportação.',
        theme: 'danger',
      });
    },
  },
  created() {
    Object.keys(this.currentConfig.types).forEach(key => {
      const isSelected = this.currentConfig.types[key].defaultSelected || false;

      this.$set(this.selectedTypes, key, isSelected);
    });
  },
};
</script>
